import { useEffect, useState } from 'react';
import { withI18n } from '@lingui/react';
import { Box, Paper, SimpleGrid, Stack, Text as MantineText } from '@mantine/core';
import { SelectWrapper } from '../../Atoms/InputFields';
import { fetchCustomAttributeGroups, fetchCustomAttributesOfGroup } from '../../Service/restapi/customAttributeService';

function getLabelAndValue(group) {
  const labelAndValues = [];
  const { attributes } = group;
  const groupName = group.name;

  if (attributes) {
    attributes.forEach((attr) => {
      labelAndValues.push({ label: `${groupName} -> ${attr.name}`, value: attr.id, attribute: attr });
    });
  }

  return labelAndValues;
}

export default withI18n()(
  ({ style, i18nPreLabel, i18n, exampleValue, customAttributesOnly, targetFieldId, field, onChange }) => {
    const [fieldOptions, setFieldOptions] = useState(
      customAttributesOnly
        ? [
            {
              label: i18n._(`${i18nPreLabel}undefined`),
              value: null,
            },
          ]
        : [
            { label: i18n._(`${i18nPreLabel}undefined`), value: 'undefined' },
            { label: i18n._(`${i18nPreLabel}date`), value: 'date' },
            { label: i18n._(`${i18nPreLabel}time.from`), value: 'time.from' },
            { label: i18n._(`${i18nPreLabel}time.to`), value: 'time.to' },
            { label: i18n._(`${i18nPreLabel}customer.emailAddress`), value: 'customer.emailAddress' },
            { label: i18n._(`${i18nPreLabel}customer.companyName`), value: 'customer.companyName' },
            { label: i18n._(`${i18nPreLabel}customer.name`), value: 'customer.name' },
            { label: i18n._(`${i18nPreLabel}customer.salutation`), value: 'customer.salutation' },
            { label: i18n._(`${i18nPreLabel}customer.firstname`, { 0: 1 }), value: 'customer.firstname' },
            { label: i18n._(`${i18nPreLabel}customer.lastname`), value: 'customer.lastname' },
            { label: i18n._(`${i18nPreLabel}customer.partner.salutation`), value: 'customer.partner.salutation' },
            { label: i18n._(`${i18nPreLabel}customer.partner.firstname`), value: 'customer.partner.firstname' },
            { label: i18n._(`${i18nPreLabel}customer.partner.lastname`), value: 'customer.partner.lastname' },
            { label: i18n._(`${i18nPreLabel}customer.partner.emailAddress`), value: 'customer.partner.emailAddress' },
            { label: i18n._(`${i18nPreLabel}partner.phone`), value: 'partner.phone' },
            { label: i18n._(`${i18nPreLabel}customer.address`), value: 'customer.address' },
            { label: i18n._(`${i18nPreLabel}customer.addressStreetAndNo`), value: 'customer.addressStreetAndNo' },
            { label: i18n._(`${i18nPreLabel}customer.addressStreet`), value: 'customer.addressStreet' },
            { label: i18n._(`${i18nPreLabel}customer.addressNo`), value: 'customer.addressNo' },
            { label: i18n._(`${i18nPreLabel}customer.postalCode`), value: 'customer.postalCode' },
            { label: i18n._(`${i18nPreLabel}customer.city`), value: 'customer.city' },
            { label: i18n._(`${i18nPreLabel}leadOrigin`), value: 'leadOrigin' },
            { label: i18n._(`${i18nPreLabel}kind`), value: 'kind' },
            { label: i18n._(`${i18nPreLabel}phone`), value: 'phone' },
            { label: i18n._(`${i18nPreLabel}requestSubject`), value: 'requestSubject' },
            { label: i18n._(`${i18nPreLabel}requestText`), value: 'requestText' },
            { label: i18n._(`${i18nPreLabel}location.name`), value: 'location.name' },
            { label: i18n._(`${i18nPreLabel}location.address`), value: 'location.address' },
            { label: i18n._(`${i18nPreLabel}location.addressStreetNo`), value: 'location.addressStreetNo' },
            { label: i18n._(`${i18nPreLabel}location.city`), value: 'location.city' },
            { label: i18n._(`${i18nPreLabel}location.zipCode`), value: 'location.zipCode' },
            { label: i18n._(`${i18nPreLabel}location.phone`), value: 'location.phone' },
          ],
    );

    useEffect(() => {
      fetchCustomAttributeGroups('event')
        .then((resp) => {
          if (!resp.ok) {
            return;
          }

          resp.json().then((groups) => {
            groups.forEach((group) => {
              fetchCustomAttributesOfGroup(group.id)
                .then((attributeResp) => {
                  if (!attributeResp.ok) {
                    return;
                  }

                  attributeResp
                    .json()
                    .then((attributes) => {
                      const tmp = getLabelAndValue({ ...group, attributes });
                      setFieldOptions((prevState) => [...prevState, ...tmp]);
                    })
                    .catch(() => undefined);
                })
                .catch(() => undefined);
            });
          });
        })
        .catch(() => undefined);
    }, []);

    function getAttributeNameById(attributeId) {
      if (attributeId) {
        const optional = fieldOptions.filter((option) => option.value === attributeId);
        if (optional && optional[0]) {
          return optional[0].label;
        }
      }

      return null;
    }

    let label;
    let value = null;
    if (targetFieldId) {
      label = getAttributeNameById(targetFieldId);
      value = targetFieldId;
    } else {
      label = i18n._(`${i18nPreLabel}undefined`);
    }

    const val = { label, value };
    const rawExampleValue = exampleValue?.value;
    const exampleText =
      rawExampleValue === undefined || rawExampleValue === null || rawExampleValue === ''
        ? 'Kein Beispielwert'
        : String(rawExampleValue);

    if (field === 'pathId' || field === 'hostname') {
      return null;
    }

    return (
      <Paper
        withBorder
        radius="md"
        p="sm"
        style={style}
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        })}
      >
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1, spacing: 'xs' }]}>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <MantineText size="sm" weight={600} className="overflow-ellipsis">
              {exampleText}
            </MantineText>
            <MantineText size="xs" color="dimmed" className="overflow-ellipsis">
              {field}
            </MantineText>
          </Stack>
          <Box sx={{ minWidth: 0, '.form-group': { marginBottom: 0 } }}>
            <SelectWrapper
              style={{ marginBottom: 0 }}
              options={fieldOptions}
              value={val}
              onChangeHandler={onChange}
              name="fields"
              noSelectionPlaceholder="document.template.select"
            />
          </Box>
        </SimpleGrid>
      </Paper>
    );
  },
);
